import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useState } from 'react';

const ENDPOINT_URL = '/usr-address/store';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetMyAccountAddressesCreateInput = paths[url]['post']['requestBody']['content']['application/json'];

export interface GetMyAccountAddressesCreateSuccessResponse {
    data: paths[url]['post']['responses']['201']['content']['application/json'];
    message: string;
}

/**
 * Requests endpoint to update an address.
 */
export const useGetMyAccountAddressesCreate = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<string | null>(null);
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setAddressesCreate } = useData();
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    // general error message (for general errors, example: 500)
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // for errors associated with form fields (422 Unprocessable Content)
    const [validationErrors, setValidationErrors] = useState<any | null>(null);

    const request = async (props: GetMyAccountAddressesCreateInput) => {
        setLoading(true);
        setSuccess(null);
        setErrorMessage(null);
        setValidationErrors(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;

                throw error;
            }

            const data: GetMyAccountAddressesCreateSuccessResponse = await response.json();

            setSuccess(data.message);
            setAddressesCreate(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setErrorMessage(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, success, errorMessage, validationErrors, lastResponseTime };
};
